<template>
    <div class="date-picker">
        <TextField
            :id="id"
            ref="pickerField"
            :model-value="dateFormatted"
            :is-readonly="isReadonly"
            :label="label"
            :placeholder="placeholder"
            :error="error"
            @click.prevent.stop="toggle"
        />

        <transition name="fade">
            <DatePickerMenu
                v-if="isOpen"
                ref="pickerMenu"
                class="date-picker-menu"
                :model-value="modelValue"
                :max="max"
                :min="min"
                :allowed-weekdays="allowedWeekdays"
                :disallowed-dates="disallowedDatesProper"
                @update:model-value="pick"
            />
        </transition>
    </div>
</template>

<script setup lang="ts">
import type { Ref } from 'vue'
import { ref, computed } from 'vue'
import { onClickOutside } from '@vueuse/core'
import { useDateFormat } from '@/composables/useDateFormat'
import { isValidDate } from '@/utils/date'
import { createTimelessDate, DAYS_ALL } from './date'

import TextField from '@/components/ui/inputs/TextField.vue'
import DatePickerMenu from './DatePickerMenu.vue'

interface Props {
    modelValue: Date | null
    label?: string
    placeholder?: string
    min?: Date | null
    max?: Date | null
    allowedWeekdays?: number
    disallowedDates?: Date[] | string[]
    isReadonly?: boolean
    error?: string
    id: string
}

const props = withDefaults(defineProps<Props>(), {
    label: undefined,
    placeholder: undefined,
    min: undefined,
    max: undefined,
    allowedWeekdays: DAYS_ALL,
    disallowedDates: () => [],
    isReadonly: false,
    error: undefined,
})

const emit = defineEmits<{
    'update:modelValue': [payload: Date | null]
}>()

const isOpen = ref(false)

const toggle = () => {
    if (!props.isReadonly) {
        isOpen.value = !isOpen.value
    }
}

const pick = (date: Date | null) => {
    emit('update:modelValue', date)

    isOpen.value = false
}

const pickerField: Ref<HTMLElement | null> = ref(null)
const pickerMenu: Ref<HTMLElement | null> = ref(null)
onClickOutside(pickerMenu, () => (isOpen.value = false), {
    ignore: [pickerField],
})

const formatDate = useDateFormat({
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
})
const dateFormatted = computed(() =>
    props.modelValue ? formatDate(props.modelValue) : ''
)

const isStringDates = (dates: string[] | Date[]): dates is string[] =>
    typeof dates[0] === 'string'

const disallowedDatesProper = computed(() => {
    if (isStringDates(props.disallowedDates)) {
        return props.disallowedDates.map((date) =>
            createTimelessDate(new Date(date))
        )
    } else if (props.disallowedDates.every((date) => isValidDate(date))) {
        return props.disallowedDates.map((date) => createTimelessDate(date))
    } else {
        return []
    }
})
</script>

<style lang="scss" scoped>
.date-picker-menu {
    position: absolute;
    z-index: 2000;
}

.fade-enter-active,
.fade-leave-active {
    transition: all 24ms var(--timing-function-easing-accelerate);
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
